import _get from 'lodash/get';
import {
  SCRIBE_STATE_NAMES,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';
import { SHOPIFY_MODE } from 'views/v2/shopify-home/constants';
import { isValidQuote } from 'helper/quote';
import calcPrice from 'common/utils/calc-price';
import { isUsingPaxPersonalPayments } from 'helper/user';
import { generateLocalisedItemOptions, getQuotesToRender } from 'helper/order';
import {
  getMsdCtaData,
  getMsdOrderSavingAmount,
} from '../common/utils/msd-utils';

export const getShopifyOrderDetails = (orders, isMSD) =>
  orders.reduce(
    ({ orderIDs, recipientNames, recipientPhones }, order) => {
      if (isMSD) {
        let ids = [];
        let names = [];
        let phones = [];
        order.fulfillmentOrders.forEach(shopifyOrder => {
          ids = [...ids, shopifyOrder.orderID];
          names = [...names, shopifyOrder.shippingAddress.name];
          phones = [...phones, shopifyOrder.shippingAddress.phone];
        });
        orderIDs = [...orderIDs, ...ids];
        recipientNames = [...recipientNames, ...names];
        recipientPhones = [...recipientPhones, ...phones];
      } else {
        const id = order.fulfillmentOrder.orderID;
        const name = order.fulfillmentOrder.shippingAddress.name;
        const phone = order.fulfillmentOrder.shippingAddress.phone;
        orderIDs = [...orderIDs, id];
        recipientNames = [...recipientNames, name];
        recipientPhones = [...recipientPhones, phone];
      }
      return { orderIDs, recipientNames, recipientPhones };
    },
    { orderIDs: [], recipientNames: [], recipientPhones: [] },
  );

export const getTotalOrderPrice = (orders, isMSD) =>
  orders.reduce((acc, order) => {
    if (isMSD) {
      order.fulfillmentOrders.forEach(shopifyOrder => {
        acc += shopifyOrder.subtotalPrice;
      });
    } else {
      acc += order.fulfillmentOrder.subtotalPrice;
    }
    return acc;
  }, 0);

export const getDropoffPOIIDs = orders =>
  orders.reduce((acc, order) => {
    const recipientLocations = order.recipientLocations.map(
      location => location.id,
    );
    acc = [...acc, ...recipientLocations];
    return acc;
  }, []);

export const getFare = orders => {
  // calculate total from single bookings
  const quotes = getQuotesToRender(orders);
  const validQuotes = [];
  const totalAmount = quotes.reduce((acc, quote) => {
    if (!isValidQuote(quote)) return acc;

    validQuotes.push(quote);
    const currentQuoteAmount = _get(quote, 'amount');
    const currentCurrency = _get(quote, 'currency');
    const currentFormattedAmount = calcPrice(
      currentQuoteAmount,
      currentCurrency,
    );
    return acc + currentFormattedAmount;
  }, 0);
  return totalAmount;
};

export const getIsPromoApplied = orders =>
  orders.map(order => _get(order, 'quote.forRender.isDiscountApplied', false));

export const getTotalWeight = orders =>
  orders.reduce((acc, order) => acc + _get(order, 'ITEM_WEIGHT.value'), 0);

export const getCategoryNames = (orders, categories) =>
  orders.map(order => {
    const category = categories.find(
      cat => cat.value === `${order.ITEM_TYPE.value}`,
    );
    return category.title;
  });

export const getSpecialInstructions = orders =>
  orders.map(order => _get(order, 'INSTRUCTION.value'));

export const getPaymentMethods = orders =>
  orders.map(order => _get(order, 'PAYMENT_METHOD.value'));

export const getPaymentTag = orders =>
  orders.map(_ => (isUsingPaxPersonalPayments() ? 'Personal' : 'Business'));

export const scribeTrackDeliveryDetailDefault = ({
  ctx,
  msdOrder,
  multipleOrders,
  mode,
}) => {
  const isMSD = mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD;
  const orders = isMSD ? [msdOrder] : multipleOrders;

  const { orderIDs } = getShopifyOrderDetails(orders, isMSD);

  const pickupPOIID = _get(orders, '[0].pickUpAddress.poiID');
  const dropOffPOIID = getDropoffPOIIDs(orders);

  const serviceTypeID = _get(orders, '[0].SERVICE_TYPE.value');
  const serviceTypes = _get(orders, '[0].serviceTypes', []) || [];
  const serviceType =
    serviceTypes.find(service => service.ID === Number(serviceTypeID)) || {};
  const currencyCode = _get(orders, '[0].quote.forRender.currency.code');
  const isMSDAvailable = _get(orders, '[0].msdInfo');

  const { totalSavings } = getMsdCtaData({ mode, multipleOrders });

  const totalFare = getFare(orders);

  ctx.fireShopifyTrackEvent({
    stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
    eventName: SCRIBE_SHOPIFY_EVENTS.DEFAULT,
    facets: {
      [SCRIBE_EVENT_PARAMS.SERVICE_TYPE]: serviceType.name,
      [SCRIBE_EVENT_PARAMS.BOOKING_MODE]: mode,
      [SCRIBE_EVENT_PARAMS.COUNT_DROPOFFS]: dropOffPOIID.length,
      [SCRIBE_EVENT_PARAMS.CURRENCY]: currencyCode,
      [SCRIBE_EVENT_PARAMS.FARE]: totalFare,
      [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: orderIDs,
      [SCRIBE_SHOPIFY_PARAMS.PICKUP_POI_ID]: pickupPOIID,
      [SCRIBE_SHOPIFY_PARAMS.DROPOFF_POI_ID]: dropOffPOIID,
      [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING]: isMSD,
      [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING_AVAILABLE]:
        isMSD || !!isMSDAvailable,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_COST_SAVED]: totalSavings,
    },
  });
};

export const scribeTrackClickGroupOrder = ({ ctx, multipleOrders, mode }) => {
  const pickupPOIID = _get(multipleOrders, '[0].pickUpAddress.poiID');
  const dropOffPOIID = getDropoffPOIIDs(multipleOrders);

  const { msdInfo, totalSavings } = getMsdCtaData({
    mode,
    multipleOrders,
  });
  const fullfillmentOrders =
    _get(msdInfo, 'selectedFulfillmentOrders', []) || [];

  const totalPrice = fullfillmentOrders.reduce((acc, order) => {
    acc += order.subtotalPrice;
    return acc;
  }, 0);

  const { orderIDs } = getShopifyOrderDetails(multipleOrders, false);

  ctx.fireShopifyTrackEvent({
    stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
    eventName: SCRIBE_SHOPIFY_EVENTS.CLICK_GROUP_ORDERS,
    facets: {
      [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: orderIDs,
      [SCRIBE_SHOPIFY_PARAMS.PICKUP_POI_ID]: pickupPOIID,
      [SCRIBE_SHOPIFY_PARAMS.DROPOFF_POI_ID]: dropOffPOIID,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_BOOKED_ORDERS]: orderIDs.length,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_PRICE]: totalPrice,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_COST_SAVED]: totalSavings,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_RECIPIENTS]: dropOffPOIID.length,
    },
  });
};

export const scribeTrackClickBookOrder = ({
  ctx,
  msdOrder,
  multipleOrders,
  mode,
  userInfo,
}) => {
  const isMSD = mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD;
  const orders = isMSD ? [msdOrder] : multipleOrders;
  const { orderIDs, recipientNames, recipientPhones } = getShopifyOrderDetails(
    orders,
    isMSD,
  );
  const dropOffPOIID = getDropoffPOIIDs(orders);
  const serviceTypeID = _get(orders, '[0].SERVICE_TYPE.value');
  const serviceTypes = _get(orders, '[0].serviceTypes', []) || [];
  const serviceType =
    serviceTypes.find(service => service.ID === Number(serviceTypeID)) || {};
  const isMSDAvailable = _get(orders, '[0].msdInfo');

  const { savingsAmount } = getMsdOrderSavingAmount({ mode, msdOrder });

  const categories = generateLocalisedItemOptions({ userInfo }).map(
    ({ id, iString }) => ({
      value: `${id}`,
      title: iString,
    }),
  );

  ctx.fireShopifyTrackEvent({
    stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
    eventName: SCRIBE_SHOPIFY_EVENTS.BOOK,
    facets: {
      [SCRIBE_EVENT_PARAMS.TAXI_TYPE]: serviceTypeID,
      [SCRIBE_EVENT_PARAMS.SERVICE_TYPE]: serviceType.name,
      [SCRIBE_EVENT_PARAMS.BOOKING_MODE]: mode,
      [SCRIBE_EVENT_PARAMS.COUNT_DROPOFFS]: dropOffPOIID.length,
      [SCRIBE_EVENT_PARAMS.CURRENCY]: _get(
        orders,
        '[0].quote.forRender.currency.code',
      ),
      [SCRIBE_EVENT_PARAMS.FARE]: getFare(orders),
      [SCRIBE_EVENT_PARAMS.RECIPIENT_NAME_TEXT]: recipientNames,
      [SCRIBE_EVENT_PARAMS.RECIPIENT_PHONE_TEXT]: recipientPhones,
      [SCRIBE_EVENT_PARAMS.ITEM_WEIGHT]: getTotalWeight(orders),
      [SCRIBE_EVENT_PARAMS.CATEGORY_NAME]: getCategoryNames(orders, categories),
      [SCRIBE_EVENT_PARAMS.TEXT]: getSpecialInstructions(orders, categories),
      [SCRIBE_EVENT_PARAMS.PAYMENT_METHOD]: getPaymentMethods(orders),
      [SCRIBE_EVENT_PARAMS.HAS_PROMO_APPLIED]: getIsPromoApplied(orders),
      [SCRIBE_SHOPIFY_PARAMS.TAG]: getPaymentTag(orders),
      [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: orderIDs,
      [SCRIBE_SHOPIFY_PARAMS.PICKUP_POI_ID]: _get(
        orders,
        '[0].pickUpAddress.poiID',
      ),
      [SCRIBE_SHOPIFY_PARAMS.DROPOFF_POI_ID]: dropOffPOIID,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_BOOKED_ORDERS]: orderIDs.length,
      [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING]: isMSD,
      [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING_AVAILABLE]:
        isMSD || !!isMSDAvailable,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_PRICE]: getTotalOrderPrice(orders, isMSD),
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_COST_SAVED]: savingsAmount,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_RECIPIENTS]: dropOffPOIID.length,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_DELIVERIES]: orders.length,
    },
  });
};

export const scribeTrackEditDelivery = ({ ctx, msdOrder }) => {
  const { orderIDs } = getShopifyOrderDetails([msdOrder], true);
  const pickupPOIID = _get([msdOrder], '[0].pickUpAddress.poiID');
  const dropOffPOIID = getDropoffPOIIDs([msdOrder]);

  ctx.fireShopifyTrackEvent({
    stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
    eventName: SCRIBE_SHOPIFY_EVENTS.EDIT_DELIVERY,
    facets: {
      [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: orderIDs,
      [SCRIBE_SHOPIFY_PARAMS.PICKUP_POI_ID]: pickupPOIID,
      [SCRIBE_SHOPIFY_PARAMS.DROPOFF_POI_ID]: dropOffPOIID,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_BOOKED_ORDERS]: orderIDs.length,
      [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING]: true,
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_RECIPIENTS]: dropOffPOIID.length,
    },
  });
};

export default scribeTrackDeliveryDetailDefault;
